import { Card, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type ExternalAccountDetailsAttributes } from "@src/appV2/redesign/Accounts/PayoutSettings/api/schema";
import { ExternalAccountLogo } from "@src/appV2/redesign/Accounts/PayoutSettings/components/ExternalAccountLogo";

import { type ShiftPaymentStatus } from "../../api/useGetShiftPaymentStatus";
import { EmptyStateText } from "../../components/EmptyStateText";
import { formatDurationFromHours, formatUsdFromCents } from "../../utils/formatters";
import { PaymentStatusPill } from "./PaymentStatusPill";
import { PaymentTimingRow } from "./PaymentTimingRow";
import { SectionDivider } from "./SectionDivider";

/**
 * Standard InstantPay fee charged per payout in cents.
 * Using cents avoids float math issues in fee calculations.
 */
const INSTANT_PAY_FEE_CENTS = 299;

interface EarningsCardProps {
  /**
   * Gross earnings in cents (converted from API dollars at the data layer).
   * Using cents avoids floating-point issues in fee calculations.
   */
  readonly grossEarningsCents: number | undefined;
  /**
   * Hourly rate in cents (converted from API dollars at the data layer).
   * Using cents maintains consistent unit handling across all money values.
   */
  readonly hourlyRateCents: number | undefined;
  readonly actualDurationHours: number | undefined;
  readonly isInstantPay: boolean;
  /**
   * Whether the shift is verified (timesheet approved).
   * Used as fallback for payment status when API data is unavailable.
   */
  readonly isVerified: boolean;
  readonly payoutDestination: ExternalAccountDetailsAttributes | undefined;
  /**
   * Payment status from the payments API.
   * Used to show accurate payment status pill matching the Earnings page.
   */
  readonly paymentStatus: ShiftPaymentStatus | undefined;
  /**
   * Whether payment status is currently loading.
   */
  readonly isPaymentStatusLoading: boolean;
}

export function EarningsCard(props: EarningsCardProps) {
  const {
    grossEarningsCents,
    hourlyRateCents,
    actualDurationHours,
    isInstantPay,
    isVerified,
    payoutDestination,
    paymentStatus,
    isPaymentStatusLoading,
  } = props;

  const hasEarnings = isDefined(grossEarningsCents);

  // Only show fee breakdown for InstantPay shifts with earnings exceeding the fee
  const hasFee = isInstantPay && hasEarnings && grossEarningsCents > INSTANT_PAY_FEE_CENTS;

  // Calculate net earnings in cents (integer math - no floating-point issues)
  const netEarningsCents = hasFee ? grossEarningsCents - INSTANT_PAY_FEE_CENTS : undefined;

  return (
    <Card variant="tertiary" sx={(theme) => ({ padding: theme.spacing(5) })}>
      <Stack spacing={3}>
        <Text semibold variant="body1">
          Earnings
        </Text>

        {hasEarnings ? (
          <EarningsDetails
            grossEarningsCents={grossEarningsCents}
            actualDurationHours={actualDurationHours}
            hourlyRateCents={hourlyRateCents}
            hasFee={hasFee}
            feeCents={INSTANT_PAY_FEE_CENTS}
            netEarningsCents={netEarningsCents}
            paymentStatus={paymentStatus}
            isVerified={isVerified}
            isInstantPay={isInstantPay}
            isPaymentStatusLoading={isPaymentStatusLoading}
            payoutDestination={payoutDestination}
          />
        ) : (
          <EmptyStateText>Earnings information isn&apos;t available yet</EmptyStateText>
        )}
      </Stack>
    </Card>
  );
}

interface EarningsDetailsProps {
  /** Gross earnings in cents. */
  readonly grossEarningsCents: number;
  readonly actualDurationHours: number | undefined;
  /** Hourly rate in cents (converted from API dollars at the data layer). */
  readonly hourlyRateCents: number | undefined;
  readonly hasFee: boolean;
  /** InstantPay fee in cents for display. */
  readonly feeCents: number;
  /** Net earnings in cents (gross - fee), undefined if no fee applies. */
  readonly netEarningsCents: number | undefined;
  readonly paymentStatus: ShiftPaymentStatus | undefined;
  readonly isVerified: boolean;
  readonly isInstantPay: boolean;
  readonly isPaymentStatusLoading: boolean;
  readonly payoutDestination: ExternalAccountDetailsAttributes | undefined;
}

function EarningsDetails(props: EarningsDetailsProps) {
  const {
    grossEarningsCents,
    actualDurationHours,
    hourlyRateCents,
    hasFee,
    feeCents,
    netEarningsCents,
    paymentStatus,
    isVerified,
    isInstantPay,
    isPaymentStatusLoading,
    payoutDestination,
  } = props;

  return (
    <>
      {/* Gross earnings row with worked duration and rate */}
      <EarningsRow
        label={formatGrossLabel(actualDurationHours, hourlyRateCents)}
        value={formatUsdFromCents(grossEarningsCents)}
      />

      {/* InstantPay fee breakdown (only if there's a fee) */}
      {hasFee && isDefined(netEarningsCents) && (
        <>
          <EarningsRow
            isDeduction
            label="InstantPay fee"
            value={`-${formatUsdFromCents(feeCents)}`}
          />
          <SectionDivider />
          <EarningsRow
            isTotal
            color="primary"
            label="Net earnings"
            value={formatUsdFromCents(netEarningsCents)}
          />
        </>
      )}

      <SectionDivider />

      {/* Payment status - matches Earnings page pill */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          Status
        </Text>
        <PaymentStatusPill
          paymentStatus={paymentStatus}
          isVerified={isVerified}
          isInstantPay={isInstantPay}
          isLoading={isPaymentStatusLoading}
        />
      </Stack>

      {/* Payment timing - shows date for paid, estimate for pending */}
      <PaymentTimingRow
        paymentStatus={paymentStatus}
        isPaymentStatusLoading={isPaymentStatusLoading}
      />

      {/* Payment destination */}
      {isDefined(payoutDestination) && (
        <>
          <SectionDivider />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
              Paid to
            </Text>
            <Stack direction="row" alignItems="center" spacing={2}>
              <ExternalAccountLogo externalAccount={payoutDestination} />
              <Text variant="body2">
                {payoutDestination.providerName ?? "Account"} ···· {payoutDestination.last4}
              </Text>
            </Stack>
          </Stack>
        </>
      )}
    </>
  );
}

interface EarningsRowProps {
  readonly label: string;
  readonly value: string;
  readonly isDeduction?: boolean;
  readonly isTotal?: boolean;
  readonly color?: "primary" | "secondary";
}

function EarningsRow(props: EarningsRowProps) {
  const { label, value, isDeduction = false, isTotal = false, color = "secondary" } = props;

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Text
        semibold={isTotal}
        variant="body2"
        sx={(theme) => ({
          color: color === "primary" ? theme.palette.text.primary : theme.palette.text.secondary,
        })}
      >
        {label}
      </Text>
      <Text
        semibold={isTotal}
        variant="body2"
        sx={(theme) => ({
          color: getValueColor(theme, isDeduction),
        })}
      >
        {value}
      </Text>
    </Stack>
  );
}

function getValueColor(
  theme: { palette: { text: { secondary: string; primary: string } } },
  isDeduction: boolean
): string {
  if (isDeduction) {
    return theme.palette.text.secondary;
  }
  return theme.palette.text.primary;
}

function formatGrossLabel(
  durationHours: number | undefined,
  hourlyRateCents: number | undefined
): string {
  if (!isDefined(durationHours) || !isDefined(hourlyRateCents)) {
    return "Gross earnings";
  }

  return `Gross earnings (${formatDurationFromHours(durationHours)} @ ${formatUsdFromCents(
    hourlyRateCents
  )}/hr)`;
}
